import { useCallback, useEffect, useRef, useState } from 'react';

import clamp from 'lodash/clamp';

import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import { closeHistory } from '@atlaskit/editor-prosemirror/history';
import { Slice, type Fragment } from '@atlaskit/editor-prosemirror/model';
import {
	TextSelection,
	type ReadonlyTransaction,
	type Transaction,
} from '@atlaskit/editor-prosemirror/state';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import { replaceConfluenceTitle } from '../../../ui/prebuilt/config-items/actions/replace-title';
import { type EditorPluginAIConfigItem } from '../../../ui/prebuilt/config-items/config-items';
import { isSuggestTitleForConfluence } from '../../../ui/prebuilt/config-items/utils/suggest-title';
import {
	type AIExperienceMachineContext,
	type Positions,
} from '../../../ui/utils/xstate/get-ai-experience-service';
import { startWatchingAnnotationChanges } from '../../ai-annotation-watchmen/transforms';
import { clearSelectionDecorations } from '../../decoration/actions';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import { inEditorDirectStreamingPluginKey } from '../in-editor-direct-streaming-plugin-key';
import {
	adjustInitPositions,
	initPositions,
	updateLoadingPositions,
	updateStreamPositions,
} from '../transforms';

const SCROLL_MARGIN = 32;

/**
 * without -1/+1 here, when we inserting a heading at the beginning of a paragraph
 * the mapped startPos will be the pos after the heading instead of the pos before the inserted heading
 *
 * we still need this even we have logic for adjusting start pos
 */
export function mapStartPos({ pos, tr }: { pos: number; tr: ReadonlyTransaction | Transaction }) {
	return clamp(tr.mapping.map(pos - 1, -1) + 1, 1, tr.doc.content.size);
}

/**
 * without -1/+1 here, when we inserting a heading at the beginning of a paragraph
 * the mapped toPos will be the pos before the heading instead of the pos after the inserted heading
 *
 * we still need this even we have logic for adjusting start pos
 */
export function mapToPos({ pos, tr }: { pos: number; tr: ReadonlyTransaction | Transaction }) {
	return clamp(tr.mapping.map(pos + 1, 1) - 1, 1, tr.doc.content.size);
}

export const useInEditorDirectStreaming = ({
	configItem,
	view,
	positions,
	modalRef,
}: {
	configItem: EditorPluginAIConfigItem;
	view: EditorView;
	positions: Positions;
	modalRef?: React.RefObject<HTMLDivElement>;
}) => {
	const isSuggestTitle = isSuggestTitleForConfluence(configItem);
	const showTelepointer = !isSuggestTitle;

	const [isPaused, setIsPaused] = useState(true);

	const [_start, setStart] = useState<number>();

	const scrollParentRef = useRef<HTMLElement | null>(null);
	const originalUserContentSlice = useRef<Slice>();
	/**
	 * The AI content slice is used to store the AI content that has been streamed
	 */
	const aiContentSlice = useRef<Slice>();

	useEffect(() => {
		// The hook may not be flagged so we flag any effects
		if (!editorExperiment('platform_editor_ai_aifc', true)) {
			return;
		}
		const dom = view.dom;
		const scrollParent = findOverflowScrollParent(dom);
		if (scrollParent) {
			scrollParentRef.current = scrollParent;
		}
	}, [view]);

	useEffect(() => {
		// The hook may not be flagged so we flag any effects
		if (!editorExperiment('platform_editor_ai_aifc', true)) {
			return;
		}
		if (_start !== undefined && scrollParentRef.current) {
			const startCoords = view.coordsAtPos(_start);
			const offsetTop = view.coordsAtPos(0).top;
			const modalHeight = modalRef?.current?.offsetHeight || 0;

			if (startCoords.top < SCROLL_MARGIN) {
				/**
				 * If the start position is above viewport,
				 * we set the scroll so that the start is at the top of viewport (+ margin)
				 */
				scrollParentRef.current.scroll({
					top: startCoords.top - offsetTop + view.dom.offsetTop - SCROLL_MARGIN,
					behavior: 'instant',
				});
			} else if (
				/**
				 * If the start position is below the viewport,
				 * we set the scroll so that the start is at the bottom of viewport (+ margin)
				 */
				startCoords.top - offsetTop + view.dom.offsetTop + SCROLL_MARGIN >
				scrollParentRef.current.offsetHeight + scrollParentRef.current.scrollTop - modalHeight
			) {
				scrollParentRef.current.scroll({
					top:
						// toPos.bottom - 0.top is height of content
						startCoords.bottom -
						offsetTop +
						// offset of view dom from scroll parent
						view.dom.offsetTop -
						// subtracting offsetHeight keeps content at the bottom so
						// auto-scrolling is more incremental
						scrollParentRef.current.offsetHeight +
						// extra margin so text isn't at the very very bottom
						SCROLL_MARGIN +
						// extra margin so modal isn't in the way
						modalHeight,
					behavior: 'instant',
				});
			}
		}
	}, [view, _start, modalRef]);

	const isEmptySelection = positions[0] === positions[1];
	const shouldReplaceDoc = isEmptySelection && !configItem.insertOnEmptySelection;
	const [start, end] = shouldReplaceDoc ? [1, view.state.doc.content.size - 1] : positions;

	/**
	 * we should show highlight when:
	 * - the selection is not empty
	 * - the selection is empty and we're inserting into the current position (i.e. free generate)
	 *
	 * we should not show highlight when:
	 * - suggest title
	 */
	const showHighlight =
		(!isEmptySelection || !!configItem.insertOnEmptySelection) && !isSuggestTitle;

	useEffect(() => {
		// The hook may not be flagged so we flag any effects
		if (!editorExperiment('platform_editor_ai_aifc', true) || isEmptySelection || !showHighlight) {
			return;
		}
		const min = 80,
			max = 240;
		const prestreamLoading = setInterval(
			() => {
				if (isPaused) {
					return;
				}
				view.dispatch(updateLoadingPositions(view.state.tr));

				if (
					(inEditorDirectStreamingPluginKey.getState(view.state) || {}).aiContentEndPos !==
					undefined
				) {
					clearInterval(prestreamLoading);
				}
			},
			Math.random() * (max - min) + min,
		);

		return () => {
			clearInterval(prestreamLoading);
		};
	}, [view, isPaused, isEmptySelection, showHighlight]);

	const mountDecorations = useCallback(() => {
		const pluginState = inEditorDirectStreamingPluginKey.getState(view.state) || {};
		if (pluginState.startPos === undefined) {
			clearSelectionDecorations(view.state)(view.state, view.dispatch);
			setIsPaused(false);
			/**
			 * Store the original user content slice so that we can restore it
			 * and use it to manipulate the history plugin.
			 */
			originalUserContentSlice.current = view.state.doc.slice(start, end);
			view.dispatch(
				closeHistory(
					initPositions({ start, end, showHighlight, showTelepointer })(
						startWatchingAnnotationChanges({ from: start, to: end })(view.state.tr),
					),
				),
			);
			setStart(start);
			return () => {
				setIsPaused(true);
			};
		}
	}, [view, start, end, showHighlight, showTelepointer]);

	/**
	 * Caller should dispatch an extra transaction to reset the document back to original state
	 * so that the history plugin can correctly track the difference between the
	 * original document and the final document after streaming.
	 */
	const restoreOriginalUserContent = useCallback(
		(tr: Transaction): Transaction => {
			const pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
			const { startPos, userContentEndPos } = pluginState || {};

			if (
				startPos === undefined ||
				userContentEndPos === undefined ||
				!originalUserContentSlice.current
			) {
				return tr;
			}

			tr = tr
				.replaceRange(startPos, userContentEndPos, originalUserContentSlice.current)
				.setMeta('addToHistory', false);

			return tr;
		},
		[view],
	);

	const metaTransaction = useCallback(
		({ tr, isFinalStream }: { tr: Transaction; isFinalStream: boolean }): Transaction => {
			tr = tr
				.setMeta('isAIStreamingTransformation', true)
				.setMeta('isAIFinalStreamingTransformation', isFinalStream)
				.setMeta('scrollIntoView', false);
			return tr;
		},
		[],
	);

	const commitFinalResponse = useCallback(
		({ tr, slice }: { tr: Transaction; slice: Slice }): Transaction => {
			const pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
			const { startPos } = pluginState || {};

			if (startPos === undefined || !originalUserContentSlice.current) {
				return tr;
			}

			tr = tr.replaceRange(
				startPos,
				startPos + (originalUserContentSlice.current.size || 0),
				slice,
			);

			tr = metaTransaction({ tr, isFinalStream: true });

			tr = tr.setSelection(
				TextSelection.create(tr.doc, Math.min(tr.selection.to, tr.doc.content.size)),
			);

			tr = closeHistory(tr);

			return tr;
		},
		[view, metaTransaction],
	);

	const posAdjusted = useRef(false);

	const getFallbackOpenStartEnd = useCallback(
		({
			startPos,
			userContentEndPos,
			fragment,
		}: {
			startPos: number;
			userContentEndPos: number;
			fragment: Fragment;
		}) => {
			/**
			 * https://discuss.prosemirror.net/t/what-is-openstart-and-openend-use-for/3999
			 *   - openStart/End are the amount of open/close tokens to skip
			 * If new block node has different type from parent node, openStart/End will be 0,
			 * 	 - we don't want to skip the open/end tokens for this
			 * Otherwise 1
			 *   - we want to skip the open/end tokens for this
			 *
			 * During streaming, we need to check both start and end to see if we need to adjust the openStart/End
			 * for example, if we insert a heading at the beginning of a paragraph,
			 * initially, the startPos and endPos are equal, both are the pos before the paragraph
			 * in this case, both openStart and openEnd should be 0
			 * but after some streaming, there is a partial heading inserted before the paragraph,
			 * startPos will be the pos before the heading, and endPos will be the pos before the paragraph
			 * in this case, openStart should be 1, and openEnd should be 0
			 */
			let fallbackOpenStart = 1;
			let fallbackOpenEnd = 1;

			/**
			 * if we are inserting a new block at the beginning/end of another block,
			 * we should find a proper pos for the new block,
			 * otherwise, there will be extra nodes splitted
			 * for example, if we insert a heading at the beginning of a list,
			 * there will be extra list item inserted
			 * so we should adjust the startPos/endPos to the pos right before/after the list
			 * but we only need to do this once for ADF streaming
			 * it may fail for markdown streaming because the streamed node type could be different
			 */
			let startPosAdjusted = startPos;
			let endPosAdjusted = userContentEndPos;

			if (isEmptySelection) {
				const $start = view.state.doc.resolve(startPos);
				const startParent = $start.parent;
				const $end = view.state.doc.resolve(userContentEndPos);
				const endParent = $end.parent;

				const newNode = fragment.firstChild;
				const oneBlock = fragment.childCount === 1 && newNode && newNode.isBlock;
				const newStartBlock = oneBlock && newNode.type.name !== startParent.type.name;
				const newEndBlock = oneBlock && newNode.type.name !== endParent.type.name;

				if (newStartBlock) {
					fallbackOpenStart = 0;
				}

				if (newEndBlock) {
					fallbackOpenEnd = 0;
				}

				if (!posAdjusted.current) {
					posAdjusted.current = true;

					if (newStartBlock) {
						for (
							let depth = $start.depth, diff = 1;
							depth > 0;
							depth--, diff++, startPosAdjusted--
						) {
							const ancestor = $start.node(depth);
							if (ancestor.type.contentMatch.matchType(newNode.type)) {
								break;
							}
							const before = $start.before(depth);
							if (before + diff !== startPos) {
								break;
							}
						}
					}

					if (newEndBlock) {
						for (let depth = $end.depth, diff = 1; depth > 0; depth--, diff++, endPosAdjusted++) {
							const ancestor = $end.node(depth);
							if (ancestor.type.contentMatch.matchType(newNode.type)) {
								break;
							}
							const after = $end.after(depth);
							if (after - diff !== userContentEndPos) {
								break;
							}
						}
					}

					if (startPosAdjusted !== startPos || endPosAdjusted !== userContentEndPos) {
						const pos =
							endPosAdjusted - userContentEndPos > startPos - startPosAdjusted
								? endPosAdjusted
								: startPosAdjusted;

						startPos = pos;
						userContentEndPos = pos;

						view.dispatch(
							adjustInitPositions({ start: startPos, end: userContentEndPos })(view.state.tr),
						);
					}
				}
			}

			return { fallbackOpenStart, fallbackOpenEnd, startPosAdjusted, endPosAdjusted };
		},
		[isEmptySelection, view],
	);

	/**
	 * We only add the final transaction to history so user can undo it all at once
	 */
	const updateStream = useCallback(
		({
			fragment,
			isFinalStream = false,
			// Prevent scroll-jacking by default to be safe
			hasUserScrolled = true,
			selectionMetaData,
		}: {
			fragment: Fragment;
			isFinalStream?: boolean;
			hasUserScrolled?: boolean;
			selectionMetaData?: AIExperienceMachineContext['selectionMetaData'];
		}) => {
			if (isSuggestTitle) {
				replaceConfluenceTitle({ editorView: view, pmFragment: fragment });
				return;
			}

			const pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
			let { startPos, userContentEndPos } = pluginState || {};
			const { aiContentEndPos } = pluginState || {};
			if (startPos === undefined || userContentEndPos === undefined) {
				return;
			}

			const { fallbackOpenStart, fallbackOpenEnd, startPosAdjusted, endPosAdjusted } =
				getFallbackOpenStartEnd({
					startPos,
					userContentEndPos,
					fragment,
				});
			startPos = startPosAdjusted;
			userContentEndPos = endPosAdjusted;

			// selectionMetaData will always be undefined when platform_editor_ai_iw_adf_streaming is disabled
			const sliceOpenStart = selectionMetaData?.sliceOpenStart ?? fallbackOpenStart;
			const sliceOpenEnd = selectionMetaData?.sliceOpenEnd ?? fallbackOpenEnd;
			// slice depth logic based on ui/prebuilt/config-items/utils/action-utils.ts
			let slice = new Slice(fragment, sliceOpenStart, sliceOpenEnd);
			// /**
			//  * Revisit later - occasionally we receive a response with empty content which causes the fragment -> slice
			//  * to have a negative size, which causes the experience to break.
			//  * At the moment, we can fallback to empty slice but user clicking 'replace' won't see any changes.
			//  */
			if (slice.size < 0) {
				slice = Slice.empty;
			}
			aiContentSlice.current = slice;

			const toPos = isFinalStream
				? userContentEndPos
				: Math.min(Math.max(aiContentEndPos || 0, startPos + slice.size + 1), userContentEndPos);

			let tr = view.state.tr;

			if (isFinalStream && originalUserContentSlice.current) {
				view.dispatch(restoreOriginalUserContent(view.state.tr));

				// Now we replace it with the AI response
				tr = commitFinalResponse({ tr: view.state.tr, slice });
			} else {
				tr = view.state.tr.replaceRange(startPos, toPos, slice).setMeta('addToHistory', false);
				tr = metaTransaction({ tr, isFinalStream: false });
			}

			const mappedStartPos = mapStartPos({ pos: startPos, tr });
			const mappedToPos = mapToPos({ pos: toPos, tr });
			let telepointerPos = mappedToPos;
			tr.doc.nodesBetween(mappedStartPos, mappedToPos, (node, pos) => {
				if (node.isLeaf) {
					telepointerPos = pos + node.nodeSize;
				}
			});
			telepointerPos = Math.min(telepointerPos, mappedToPos);

			view.dispatch(
				updateStreamPositions({ aiContentEndPos: toPos, telepointerPos, isFinalStream })(tr),
			);

			const modalHeight = modalRef?.current?.offsetHeight || 0;
			if (
				!hasUserScrolled &&
				scrollParentRef.current &&
				scrollParentRef.current.offsetHeight - modalHeight < view.coordsAtPos(toPos).bottom
			) {
				scrollParentRef.current.scroll({
					top:
						// toPos.bottom - 0.top is height of content
						view.coordsAtPos(toPos).bottom -
						view.coordsAtPos(0).top +
						// offset of view dom from scroll parent
						view.dom.offsetTop -
						// subtracting offsetHeight keeps content at the bottom so
						// auto-scrolling is more incremental
						scrollParentRef.current.offsetHeight +
						// extra margin so text isn't at the very very bottom
						SCROLL_MARGIN +
						// extra margin so modal isn't in the way
						modalHeight,
					behavior: 'instant',
				});
			}
		},
		[
			view,
			modalRef,
			isSuggestTitle,
			restoreOriginalUserContent,
			commitFinalResponse,
			metaTransaction,
			getFallbackOpenStartEnd,
		],
	);

	const discardStream = useCallback(() => {
		const pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
		const { startPos, userContentEndPos } = pluginState || {};

		if (
			startPos === undefined ||
			userContentEndPos === undefined ||
			!originalUserContentSlice.current
		) {
			return;
		}

		let tr = view.state.tr;

		// we only discard if we already have streamed AI content
		// which means we don't discard if there is no network response yet or we haven't streamed anything yet
		if (!!aiContentSlice.current) {
			tr = restoreOriginalUserContent(tr);
		}

		// restore user selection
		let anchor = startPos;
		let head = startPos + (originalUserContentSlice.current.size || 0);
		const decorationPluginState = aiExperienceDecorationPluginKey.getState(view.state);
		if (
			decorationPluginState &&
			decorationPluginState.initialSelection &&
			decorationPluginState.initialSelection.head < decorationPluginState.initialSelection.anchor
		) {
			[anchor, head] = [head, anchor];
		}
		tr.setSelection(TextSelection.create(tr.doc, anchor, head));

		view.dispatch(tr);
	}, [view, restoreOriginalUserContent]);

	/**
	 * we can commit AI stream when we encounter error during streaming
	 * user can manually undo it
	 */
	const commitStream = useCallback(() => {
		// we only commit if we have already streamed some AI content
		if (!aiContentSlice.current) {
			return;
		}

		view.dispatch(restoreOriginalUserContent(view.state.tr));

		// commit AI response
		const tr = commitFinalResponse({ tr: view.state.tr, slice: aiContentSlice.current });
		view.dispatch(tr);
	}, [view, restoreOriginalUserContent, commitFinalResponse]);

	return {
		mountDecorations,
		updateStream,
		discardStream,
		commitStream,
	};
};
